import { useState } from 'react';
import { Wrench, Plus, Search, X, Pencil, Trash2, Table2, Rows3 } from 'lucide-react';
import { FormModal } from '../../components/ui/FormModal';
import { Button, Toast } from '../../components/ui/FormField';
import { Field, TextInput, TextareaInput, PrimaryButton, GhostButton } from '../../components/ui/Field';
import { SortHeader, makeSortToggle } from '../../components/ui/SortHeader';
import { IconButton, Cell, PaginationControls, GradientOverlayActions, ViewModeSwitch } from '../../components/ui/grid';
import { useCrudPage } from '../../hooks/useCrudPage';
import { useAutoCreate } from '../../hooks/useAutoCreate';
import { usePageHeader } from '../../stores/page-store';
import { useActivityStore } from '../../stores/activity-store';
import { toolModelApi } from '../../services/api-services';
import { useAuthStore } from '../../stores/auth-store';
import type { ToolModel } from '../../types/api';

type TmView = 'comfortable' | 'compact';
const VIEW_MODE_KEY = 'toolmodels.viewMode';
function loadTmView(): TmView {
  try { const v = localStorage.getItem(VIEW_MODE_KEY); if (v === 'compact' || v === 'comfortable') return v; } catch { /* ignore */ }
  return 'comfortable';
}

const empty: Partial<ToolModel> = { toolModelName: '', model: '', description: '' };

export function ToolModelsPage() {
  const authUser = useAuthStore((s) => s.user);
  const canEdit = authUser?.role === 'SuperAdmin' || authUser?.role === 'CompanyAdmin';
  const recordActivity = useActivityStore((s) => s.record);

  const crud = useCrudPage<ToolModel>({ fetchFn: toolModelApi.getAll });
  const [form, setForm] = useState<Partial<ToolModel>>(empty);
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState('');
  const [modelError, setModelError] = useState('');
  const [descError, setDescError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const [viewMode, setViewModeRaw] = useState<TmView>(loadTmView);
  const setViewMode = (v: TmView) => { setViewModeRaw(v); try { localStorage.setItem(VIEW_MODE_KEY, v); } catch { /* ignore */ } };
  const compact = viewMode === 'compact';

  const openEdit = (item: ToolModel) => { setForm({ ...item }); setNameError(''); setModelError(''); setDescError(''); setSubmitted(false); crud.openEdit(item); };
  const openCreate = () => { setForm({ ...empty }); setNameError(''); setModelError(''); setDescError(''); setSubmitted(false); crud.openCreate(); };
  useAutoCreate(openCreate, canEdit);

  const handleSave = async () => {
    setSubmitted(true);
    let hasError = false;
    if (!form.toolModelName?.trim()) { setNameError('Name is required'); hasError = true; } else { setNameError(''); }
    if (!form.model?.trim()) { setModelError('Model is required'); hasError = true; } else { setModelError(''); }
    if (!form.description?.trim()) { setDescError('Description is required'); hasError = true; } else { setDescError(''); }
    if (hasError) return;
    setSaving(true);
    try {
      const result = crud.editingItem ? await toolModelApi.update(form) : await toolModelApi.create(form);
      if (result?.status === 'Failed' || result?.error) {
        const msg = result?.error?.errorMessage || 'Failed to save';
        if (/model number/i.test(msg)) setModelError(msg);
        crud.showToast(msg, 'error');
        return;
      }
      const label = form.toolModelName?.trim() || 'Tool model';
      crud.showToast(crud.editingItem ? 'Tool model updated' : 'Tool model created');
      recordActivity({ type: crud.editingItem ? 'update' : 'create', entity: 'Tool Models', label, user: authUser?.username });
      crud.closeModal(); crud.load();
    } catch { crud.showToast('Failed to save', 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (item: ToolModel) => {
    if (!confirm('Delete this tool model?')) return;
    try {
      await toolModelApi.delete(item.id);
      crud.showToast('Deleted');
      recordActivity({ type: 'delete', entity: 'Tool Models', label: item.toolModelName?.trim() || `Tool model #${item.id}`, user: authUser?.username });
      crud.load();
    }
    catch { crud.showToast('Failed to delete', 'error'); }
  };

  const handleSort = makeSortToggle(crud.sortCol, crud.sortDir, crud.setSortCol, crud.setSortDir);

  usePageHeader(
    'Tool Models',
    canEdit ? <Button onClick={openCreate}><Plus size={16} /> Add Tool Model</Button> : null,
    [canEdit],
  );

  const totalPages = Math.max(1, Math.ceil(crud.totalCount / crud.pageSize));
  const currentPage = Math.floor(crud.offset / crud.pageSize) + 1;

  const colTemplate = compact
    ? 'minmax(200px,1.2fr) minmax(180px,1fr) minmax(260px,2fr)'
    : (canEdit
        ? 'minmax(180px,1.2fr) minmax(160px,1fr) minmax(240px,2fr) 116px'
        : 'minmax(180px,1.2fr) minmax(160px,1fr) minmax(240px,2fr)');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      <div style={{ background: 'var(--color-panel)', border: '1px solid var(--color-border)', borderRadius: 10, width: 'fit-content', minWidth: '100%' }}>
        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid var(--color-border)', flexWrap: 'wrap' }}>
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', background: 'var(--color-panel-alt)', border: '1px solid var(--color-border)', borderRadius: 8, width: 280 }}
            className="focus-within:!border-[var(--color-accent-bright)]"
          >
            <Search size={15} style={{ color: 'var(--color-text-dim)' }} />
            <input
              placeholder="Search tool models…"
              value={crud.searchText}
              onChange={(e) => { crud.setSearchText(e.target.value); crud.setOffset(0); }}
              style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13.5, flex: 1, fontFamily: 'var(--font-body)', color: 'var(--color-text)', width: '100%' }}
            />
            {crud.searchText && (
              <button onClick={() => { crud.setSearchText(''); crud.setOffset(0); }} aria-label="Clear search" title="Clear search"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: 9, padding: 0, background: 'transparent', border: 'none', color: 'var(--color-text-dim)', cursor: 'pointer', flexShrink: 0 }}>
                <X size={13} />
              </button>
            )}
          </div>

          <div style={{ flex: 1 }} />

          <ViewModeSwitch
            value={viewMode}
            onChange={setViewMode}
            options={[{ id: 'comfortable', tip: 'Comfortable view', Icon: Table2 }, { id: 'compact', tip: 'Compact view', Icon: Rows3 }]}
          />

          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--color-text-dim)' }}>
            {crud.loading ? 'Loading…' : `${crud.totalCount} record${crud.totalCount === 1 ? '' : 's'}`}
          </div>
        </div>

        {/* Header + rows */}
        <div >
          <div
            className={compact ? 'grid-bordered-compact' : 'grid-bordered'}
            style={{
              display: 'grid',
              gridTemplateColumns: colTemplate,
              columnGap: 0,
              padding: 0,
              alignItems: 'stretch',
              borderBottom: '1px solid var(--color-border)',
              background: 'var(--color-panel-alt)',
              position: 'sticky',
              top: 72,
              zIndex: 10,
              fontSize: 14,
              letterSpacing: 0.3,
              color: 'var(--color-text)',
              fontWeight: 600,
              fontFamily: 'var(--font-body)',
            }}
          >
            <SortHeader label="Name" col="ToolName" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
            <SortHeader label="Model" col="Model" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
            <SortHeader label="Description" col="Description" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
            {!compact && canEdit && <div style={{ textAlign: 'right' }}>Actions</div>}
          </div>

          {crud.loading ? (
            <div style={{ padding: 60, textAlign: 'center', color: 'var(--color-text-dim)', fontSize: 13 }}>Loading…</div>
          ) : crud.data.length === 0 ? (
            <div style={{ padding: 60, textAlign: 'center', color: 'var(--color-text-dim)' }}>
              <Wrench size={32} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
              <div style={{ fontSize: 14, marginBottom: 4 }}>No tool models found</div>
              <div style={{ fontSize: 12 }}>{canEdit ? 'Add a new tool model to get started.' : 'No tool models to display.'}</div>
            </div>
          ) : (
            <div>
              {crud.data.map((m, i) => (
                <ToolModelRow
                  key={m.id ?? i}
                  model={m}
                  compact={compact}
                  canEdit={canEdit}
                  colTemplate={colTemplate}
                  isLast={i === crud.data.length - 1}
                  onEdit={() => openEdit(m)}
                  onDelete={() => handleDelete(m)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Pagination footer */}
        <div style={{ padding: '12px 18px', borderTop: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: 'var(--color-text-dim)', background: 'var(--color-panel-alt)', borderRadius: '0 0 10px 10px' }}>
          <div>
            Showing <span style={{ color: 'var(--color-text-mid)', fontWeight: 500 }}>{crud.data.length}</span>{' '}
            of <span style={{ color: 'var(--color-text-mid)', fontWeight: 500 }}>{crud.totalCount}</span> tool models
          </div>
          <PaginationControls currentPage={currentPage} totalPages={totalPages} onPage={(p) => crud.setOffset((p - 1) * crud.pageSize)} />
        </div>
      </div>

      {/* Form modal — flat */}
      <FormModal
        open={crud.modalOpen}
        onClose={crud.closeModal}
        title={crud.editingItem ? 'Edit Tool Model' : 'New Tool Model'}
        icon={<Wrench size={20} />}
        footer={
          <>
            <GhostButton onClick={crud.closeModal} disabled={saving}>Cancel</GhostButton>
            <PrimaryButton onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : crud.editingItem ? 'Save changes' : 'Add Tool Model'}
            </PrimaryButton>
          </>
        }
      >
        <Field label="Name" required error={submitted ? nameError : undefined}>
          <TextInput
            value={form.toolModelName ?? ''}
            placeholder="e.g. Perigon Acoustic 8 Sensor"
            error={submitted && !!nameError}
            onChange={(e) => { setForm(p => ({ ...p, toolModelName: e.target.value })); if (submitted && e.target.value.trim()) setNameError(''); }}
          />
        </Field>
        <Field label="Model" required error={submitted ? modelError : undefined}>
          <TextInput
            value={form.model ?? ''}
            placeholder="e.g. PAS-8V1"
            error={submitted && !!modelError}
            onChange={(e) => { setForm(p => ({ ...p, model: e.target.value })); if (submitted && e.target.value.trim()) setModelError(''); }}
          />
        </Field>
        <Field label="Description" required error={submitted ? descError : undefined}>
          <TextareaInput
            value={form.description ?? ''}
            placeholder="Describe this tool model…"
            error={submitted && !!descError}
            onChange={(e) => { setForm(p => ({ ...p, description: e.target.value })); if (submitted && e.target.value.trim()) setDescError(''); }}
          />
        </Field>
      </FormModal>

      {crud.toast && <Toast message={crud.toast.message} type={crud.toast.type} onClose={() => crud.setToast(null)} />}
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────

function ToolModelRow({
  model: m, compact, canEdit, colTemplate, isLast, onEdit, onDelete,
}: {
  model: ToolModel; compact: boolean; canEdit: boolean; colTemplate: string; isLast: boolean;
  onEdit: () => void; onDelete: () => void;
}) {
  const [hover, setHover] = useState(false);
  const rowBg = hover ? 'var(--color-panel-alt)' : (compact ? 'var(--color-panel)' : 'transparent');
  const wrap = { overflowWrap: 'anywhere' as const, wordBreak: 'break-word' as const };

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={compact ? 'grid-bordered-compact' : 'grid-bordered'}
      style={{
        display: 'grid',
        gridTemplateColumns: colTemplate,
        columnGap: 0,
        padding: 0,
        borderBottom: isLast ? 'none' : '1px solid var(--color-border)',
        alignItems: 'stretch',
        fontSize: 14,
        position: compact ? 'relative' : undefined,
        background: rowBg,
        transition: 'background 120ms',
      }}
    >
      <Cell style={{ ...wrap, fontWeight: 500 }}>{m.toolModelName || '—'}</Cell>
      <Cell style={{ ...wrap, fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--color-text-mid)' }}>{m.model || '—'}</Cell>
      <Cell style={{ ...wrap, color: 'var(--color-text-mid)' }}>{m.description || '—'}</Cell>

      {!compact && canEdit && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
          <IconButton title="Edit" onClick={onEdit}><Pencil size={17} /></IconButton>
          <IconButton title="Delete" onClick={onDelete} hoverDanger><Trash2 size={17} /></IconButton>
        </div>
      )}
      {compact && canEdit && (
        <GradientOverlayActions visible={hover} bgColor={rowBg} onEdit={onEdit} onDelete={onDelete} />
      )}
    </div>
  );
}
